import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { CheckCircle2, RefreshCcw, XCircle } from 'lucide-react'
import { Button } from '@shared/ui/Button'
import { AdaptiveSheet } from '@shared/ui/AdaptiveSheet'
import { ROUTES } from '@shared/config/routes'
import { ENV } from '@shared/config/env'
import { PROFILE_QUERY_KEY } from '@entities/user'
import { PAYMENT_METHODS_QUERY_KEY } from '../../model/usePaymentMethodsQuery'
import { usePaymentStatusQuery } from '../../model/usePaymentStatusQuery'
import { usePaymentTrackingParam } from '../../model/usePaymentTrackingParam'
import type { PaymentStatus } from '../../api/types'
import styles from './PaymentStatusModal.module.css'

const PENDING_WARNING_DELAY_MS = 3 * 60_000
const VALID_DEBUG_STATUSES: PaymentStatus[] = [
  'pending',
  'success',
  'failed',
  'cancelled',
  'refunded',
]

const isFinal = (s: PaymentStatus) => s !== 'pending'

/**
 * Глобальная модалка статуса платежа. Монтируется в WebAppShell/TgAppShell —
 * показывается, пока в URL есть `?payment=<tracking_uuid>`. Закрытие убирает
 * этот параметр и оставляет пользователя на той же странице.
 *
 * На `success` редирект на /profile (заодно убирает payment-параметр).
 * Дев-оверрайд: `?debugStatus=success|failed|cancelled|refunded|pending` (в DEV-режиме).
 */
export const PaymentStatusModal = () => {
  const { trackingUuid, clearTrackingUuid } = usePaymentTrackingParam()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showSlowWarning, setShowSlowWarning] = useState(false)

  const debugStatusParam = searchParams.get('debugStatus') as PaymentStatus | null
  const debugStatus =
    ENV.IS_DEV && debugStatusParam && VALID_DEBUG_STATUSES.includes(debugStatusParam)
      ? debugStatusParam
      : null

  const isOpen = Boolean(trackingUuid) || Boolean(debugStatus)

  const { data, error, refetch, isLoading } = usePaymentStatusQuery(
    debugStatus || !trackingUuid ? undefined : trackingUuid
  )
  const status: PaymentStatus | undefined = debugStatus ?? data?.status

  // На финальном `success` инвалидируем профиль, список карт и подписку.
  // ['subscription'] нужен, чтобы useSubscription перезапросился и записал
  // в стор актуальные статус/дату — иначе они обновятся лишь после reload.
  useEffect(() => {
    if (status === 'success') {
      qc.invalidateQueries(PROFILE_QUERY_KEY)
      qc.invalidateQueries(PAYMENT_METHODS_QUERY_KEY)
      qc.invalidateQueries(['subscription'])
    }
  }, [status, qc])

  // Через 3 минуты pending — показываем «Платёж занимает дольше обычного».
  useEffect(() => {
    if (!isOpen) return
    if (status && isFinal(status)) {
      setShowSlowWarning(false)
      return
    }
    const timer = setTimeout(() => setShowSlowWarning(true), PENDING_WARNING_DELAY_MS)
    return () => clearTimeout(timer)
  }, [status, isOpen])

  const close = () => {
    setShowSlowWarning(false)
    clearTrackingUuid()
  }

  const goProfile = () => {
    setShowSlowWarning(false)
    clearTrackingUuid()
    navigate(ROUTES.PROFILE)
  }

  if (!isOpen) return null

  // 404 от сервера — платёж не наш или не существует.
  const is404 =
    !debugStatus &&
    (error as { response?: { status?: number } } | undefined)?.response?.status === 404
  if (is404) {
    return (
      <AdaptiveSheet onClose={close} height="fixed">
        <div className={styles.body}>
          <div className={`${styles.iconWrapper} ${styles.iconFailed}`}>
            <XCircle size={48} />
          </div>
          <h2 className={styles.title}>Платёж не найден</h2>
          <p className={styles.text}>
            Возможно, платёж устарел или открыт не из вашего аккаунта.
          </p>
          <div className={styles.actions}>
            <Button variant="primary" onClick={goProfile} block>В профиль</Button>
          </div>
        </div>
      </AdaptiveSheet>
    )
  }

  if (status === 'success') {
    return (
      <AdaptiveSheet onClose={goProfile} height="fixed">
        <div className={styles.body}>
          <div className={`${styles.iconWrapper} ${styles.iconSuccess}`}>
            <CheckCircle2 size={48} />
          </div>
          <h2 className={styles.title}>Оплата прошла успешно</h2>
          <p className={styles.text}>Спасибо! Доступ уже активирован.</p>
          <div className={styles.actions}>
            <Button variant="primary" onClick={goProfile} block>Вернуться в профиль</Button>
          </div>
        </div>
      </AdaptiveSheet>
    )
  }

  if (status === 'failed') {
    return (
      <AdaptiveSheet onClose={close} height="fixed">
        <div className={styles.body}>
          <div className={`${styles.iconWrapper} ${styles.iconFailed}`}>
            <XCircle size={48} />
          </div>
          <h2 className={styles.title}>Не удалось провести платёж</h2>
          <p className={styles.text}>
            Попробуйте ещё раз или используйте другой способ оплаты.
          </p>
          <div className={styles.actions}>
            <Button variant="primary" onClick={close} block>Попробовать ещё раз</Button>
          </div>
        </div>
      </AdaptiveSheet>
    )
  }

  if (status === 'cancelled') {
    return (
      <AdaptiveSheet onClose={close} height="fixed">
        <div className={styles.body}>
          <div className={`${styles.iconWrapper} ${styles.iconFailed}`}>
            <XCircle size={48} />
          </div>
          <h2 className={styles.title}>Платёж отменён</h2>
          <p className={styles.text}>
            Вы прервали оплату. Можете попробовать ещё раз.
          </p>
          <div className={styles.actions}>
            <Button variant="primary" onClick={close} block>Назад</Button>
          </div>
        </div>
      </AdaptiveSheet>
    )
  }

  if (status === 'refunded') {
    return (
      <AdaptiveSheet onClose={close} height="fixed">
        <div className={styles.body}>
          <div className={`${styles.iconWrapper} ${styles.iconFailed}`}>
            <RefreshCcw size={48} />
          </div>
          <h2 className={styles.title}>Платёж возвращён</h2>
          <p className={styles.text}>
            По этому платежу был оформлен возврат.
          </p>
          <div className={styles.actions}>
            <Button variant="primary" onClick={goProfile} block>Вернуться в профиль</Button>
          </div>
        </div>
      </AdaptiveSheet>
    )
  }

  // pending или ещё не пришёл первый ответ
  return (
    <AdaptiveSheet onClose={close} height="fixed">
      <div className={styles.body}>
        <h2 className={styles.titleAccent}>Создание платежа</h2>
        <div className={styles.spinner} aria-hidden />
        <p className={styles.textPending}>Создаём ссылку на оплату</p>

        {showSlowWarning && (
          <>
            <p className={styles.warning}>Платёж занимает дольше обычного.</p>
            <div className={styles.actions}>
              <Button variant="secondary" onClick={() => refetch()} disabled={isLoading} block>
                Обновить статус
              </Button>
              <Button variant="link" onClick={goProfile}>В профиль</Button>
            </div>
          </>
        )}
      </div>
    </AdaptiveSheet>
  )
}
